import cx from 'clsx';
import { addMinutes, format } from 'date-fns';
import React from 'react';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import type { IStatusItem, ITooltipData } from './model/statusItem';
import style from './style.module.scss';

interface IProps {
  item: IStatusItem;
  hideTroubleshooting?: boolean;
  onlyTitle?: boolean;
  shortVariant?: boolean;
  showCommentsCount?: boolean;
  showSnoozeAlways?: boolean;
  tooltipId?: string;
}

const StatusTypeItem: React.FC<IProps> = ({
  item,
  hideTroubleshooting,
  onlyTitle,
  shortVariant,
  showCommentsCount,
  showSnoozeAlways,
  tooltipId,
}) => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();

  if (!item) return null;

  const stateName = item.stateName;
  const urgencyVariant = item.urgencyName.toLowerCase();

  const getSnoozeDateTime = () => {
    const remindDate = item.remindDate;

    if (!remindDate) {
      return '';
    }
    const userTimezone = -new Date().getTimezoneOffset();

    return format(
      addMinutes(remindDate, userTimezone),
      `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`,
    );
  };

  const snoozeDateTime = stateName === 'Snoozed' && !shortVariant ? getSnoozeDateTime() : null;
  const isArchived = stateName === 'Archived';
  const shouldShowCommentsCount = showCommentsCount && !!item.commentsCount;

  const getTooltipData = () => {
    if (onlyTitle || isArchived) {
      return null;
    }
    const tooltipData: ITooltipData = {
      stateName: item.stateName,
      urgencyName: item.urgencyName,
      snoozeDateTime,
    };

    return JSON.stringify(tooltipData);
  };

  if (!stateName) {
    return null;
  }

  return (
    <div id="tourStep-18" className={style.statusAiBlock}>
      {!!item.is_troubleshooting_hold && !hideTroubleshooting && (
        <div
          className={cx(style.statusWrapper, style.troubleshooting, {
            [style.rightBorder]: !shouldShowCommentsCount,
          })}
        >
          <span
            className={cx(style.statusAi, {
              [style.rightBorder]: shouldShowCommentsCount,
            })}
          >
            {t('Troubleshooting hold')}
          </span>
          {shouldShowCommentsCount && <span className={style.commentsCount}>{item.commentsCount}</span>}
        </div>
      )}
      {item.has_declined_report && !item.is_troubleshooting_hold && (
        <div className={cx(style.statusWrapper, style.declined)}>
          <span className={style.statusAi} title={t('Declined Completion Report')}>
            {t('Report Declined')}
          </span>
        </div>
      )}
      {!item.is_troubleshooting_hold && (
        <div
          className={cx(style.statusWrapper, style[urgencyVariant], {
            [style.archived]: isArchived,
          })}
        >
          <span
            data-qa="status"
            data-tooltip-place="right"
            data-tooltip-id={tooltipId}
            data-tooltip-content={getTooltipData()}
            className={style.statusAi}
          >
            {t(stateName)}
            {!!snoozeDateTime && (
              <span className={cx({ [style.hideSnoozeDateTime]: !showSnoozeAlways })}>{` ${t(
                'until',
              )} ${snoozeDateTime}`}</span>
            )}
          </span>
          {shouldShowCommentsCount && <span className={style.commentsCount}>{item.commentsCount}</span>}
        </div>
      )}
    </div>
  );
};

export default StatusTypeItem;
